import {useRouter} from 'next/router';
import {useCallback, useState} from 'react';
import {tt} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import {twMerge} from '../../common/twMerge';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import {
  LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
  VIEWER_TOAST_MESSAGES,
  ViewerType,
} from '../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import {useViewerMenuHandler} from '../../domain/content/[seriesId]/viewer/[productId]/hook/useViewerMenuHandler';
import {changeReadType as changeImageReadType} from '../../domain/content/[seriesId]/viewer/[productId]/state/imageViewerSlice';
import {changeReadType as changeTextReadType} from '../../domain/content/[seriesId]/viewer/[productId]/state/textViewerSlice';
import {
  setCurrentViewerGuide,
  setNonstopWatching,
} from '../../domain/content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {ImageViewerReadType} from '../../domain/content/[seriesId]/viewer/[productId]/type/ImageViewerReadType';
import {TextViewerReadType} from '../../domain/content/[seriesId]/viewer/[productId]/type/TextViewerReadType';
import {convertViewerReadTypeToGuideType} from '../../domain/content/[seriesId]/viewer/[productId]/util/convertViewerReadTypeToGuideType';
import {saveViewerSettingToLocalStorage} from '../../domain/content/[seriesId]/viewer/[productId]/util/saveViewerSettingToLocalStorage';
import {showViewerGuide} from '../../domain/content/[seriesId]/viewer/[productId]/util/showViewerGuide';
import {
  AgeGrade,
  ReadType,
  SaleMethod,
  SlideType,
  ViewDirection,
} from '../../graphql/generated/types';
import {putTiaraObjectProps} from '../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../reactQuery/constant';
import {showToast} from '../../toast/toastHandler';
import {useSinglePreviewQuery} from '../../wrapper';
import {useContentHomeOverviewQuery} from '../../wrapper/contentWrapper';
import {useViewerInfoQuery} from '../../wrapper/viewerWrapper';
import {NonstopWatchingDialog} from '../organisms/NonstopWatchingDialog/NonstopWatchingDialog';
import {PcViewerHeader} from '../organisms/PcViewerHeader/PcViewerHeader';

export function PcLayoutViewerHeader() {
  const router = useRouter();
  const dispatch = useTypedDispatch();

  const {isMobile} = requestContext.requestInfo.display;

  const {productId, seriesId} = router.query;
  const currentProductId = Number(productId);
  const currentSeriesId = Number(seriesId);
  const previewKey = String(router.query?.k || '');
  const isCashTypeViewer =
    useContentHomeOverviewQuery({seriesId: currentSeriesId}, ONLY_DATA_CONFIG)
      .data?.contentHomeOverview?.content?.saleMethod === SaleMethod.Cash;

  const {data, isFetching, isError} = useViewerInfoQuery(
    {
      seriesId: currentSeriesId,
      productId: currentProductId,
    },
    ONLY_DATA_CONFIG
  );
  const viewerData = data;
  const commentCount = viewerData?.item.serviceProperty?.commentCount;

  const {cmsViewerData, isNonstopWatching} = useTypedSelector(state => ({
    cmsViewerData: previewKey
      ? state.viewerInfo.shortVodViewerPreview[previewKey]?.data
      : undefined,
    isNonstopWatching:
      !!state.viewerInfo.nonstopWatchingInSeries[currentSeriesId]
        ?.isNonstopWatching,
  }));
  const isShortsDrama =
    cmsViewerData?.item.isShortsDrama ?? !!viewerData?.item.isShortsDrama;

  const {isViewerMenuExposed} = useTypedSelector(state => ({
    isViewerMenuExposed: state.viewerMenu.isExposed,
  }));

  const {
    textViewerSettings,
    imageViewerSettings,
    isReverseViewDirection,
    isScrollOnlyImageViewer,
    hideAgeGradeLabel,
  } = useTypedSelector(state => ({
    textViewerSettings: state.textViewer.settings,
    imageViewerSettings: state.imageViewer.settings,
    isReverseViewDirection:
      state.imageViewer.viewData[currentProductId]?.downloadData
        ?.viewDirection === ViewDirection.Backward,
    isScrollOnlyImageViewer:
      state.imageViewer.viewData[currentProductId]?.downloadData?.readType ===
      ReadType.Scroll, // 스크롤만 가능한 회차의 경우 서버에서 주는 ReadType이 Scroll
    hideAgeGradeLabel: state.viewerInfo.hideAgeGradeLabel,
  }));

  const [isOpenNonstopWatchingDialog, setIsOpenNonstopWatchingDialog] =
    useState(false);

  const {handleMovePrevProduct, handleMoveNextProduct} = useViewerMenuHandler({
    isMobile,
  });

  // PC버전 스크롤 뷰타입 설정
  const checkPcIsScrollView = useCallback(() => {
    if (!viewerData) return false;

    if (viewerData.item.slideType === SlideType.Epub) {
      return textViewerSettings.readType === TextViewerReadType.Scroll;
    } else {
      return imageViewerSettings.readType === ImageViewerReadType.Scroll;
    }
  }, [imageViewerSettings.readType, textViewerSettings.readType, viewerData]);

  // PC버전 열람 방식 변경 버튼으로 조작해도 로컬스토리지에 저장하고 뷰어 가이드를 노출하여야 함.
  const onClickViewTypeButton = useCallback(() => {
    if (!viewerData) return false;

    if (viewerData.item.slideType === SlideType.Epub) {
      const changedReadType =
        textViewerSettings.readType === TextViewerReadType.Page
          ? TextViewerReadType.Scroll
          : TextViewerReadType.Page;
      saveViewerSettingToLocalStorage(
        ViewerType.TextViewer,
        LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
        changedReadType
      );
      dispatch(changeTextReadType(changedReadType));
      dispatch(
        setCurrentViewerGuide(
          convertViewerReadTypeToGuideType({
            viewerType: ViewerType.TextViewer,
            viewerReadType: changedReadType,
          })
        )
      );
      showViewerGuide({
        viewerGuideType: convertViewerReadTypeToGuideType({
          viewerType: ViewerType.TextViewer,
          viewerReadType: changedReadType,
        }),
      });
    } else {
      const changedReadType =
        imageViewerSettings.readType === ImageViewerReadType.Page
          ? ImageViewerReadType.Scroll
          : ImageViewerReadType.Page;
      saveViewerSettingToLocalStorage(
        ViewerType.ImageViewer,
        LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
        changedReadType
      );
      dispatch(changeImageReadType(changedReadType));
      const changedGuidType = convertViewerReadTypeToGuideType({
        viewerType: ViewerType.ImageViewer,
        viewerReadType: changedReadType,
        isReverseViewDirection,
      });

      dispatch(setCurrentViewerGuide(changedGuidType));
      showViewerGuide({
        viewerGuideType: changedGuidType,
      });
      // 가이드를 띄웠으면 현 상태를 업데이트 해주어야 함
      dispatch(setCurrentViewerGuide(changedGuidType));
    }
  }, [
    dispatch,
    imageViewerSettings.readType,
    isReverseViewDirection,
    textViewerSettings.readType,
    viewerData,
  ]);

  const handleNonstopWatchingOff = useCallback(() => {
    if (currentSeriesId) {
      dispatch(
        setNonstopWatching({
          seriesId: currentSeriesId,
          isNonstopWatching: false,
        })
      );
      showToast({
        message: tt(VIEWER_TOAST_MESSAGES.nonStopWatchingOff),
        isMobile: requestContext.requestInfo.display.isMobile,
      });
    }
  }, [dispatch, currentSeriesId]);

  const onClickNonStopWatchingButton = useCallback(() => {
    // 이미 런모드가 활성화 되어있으면 Off, 아닐경우 팝업을 띄워 정주행을 진행할지 물어봅니다
    if (!isNonstopWatching) {
      setIsOpenNonstopWatchingDialog(true);
    } else {
      handleNonstopWatchingOff();
    }
  }, [handleNonstopWatchingOff, isNonstopWatching]);

  const handleNonstopWatchingOn = useCallback(() => {
    if (currentSeriesId) {
      dispatch(
        setNonstopWatching({
          seriesId: currentSeriesId,
          isNonstopWatching: true,
        })
      );
      setIsOpenNonstopWatchingDialog(false);
      showToast({
        message: tt(VIEWER_TOAST_MESSAGES.nonStopWatchingOn),
        isMobile: requestContext.requestInfo.display.isMobile,
      });
    }
  }, [dispatch, currentSeriesId]);

  const singlePreviewResult = useSinglePreviewQuery({
    productId: currentProductId,
  });
  const {data: singlePreviewData} = singlePreviewResult;

  const productTitle = singlePreviewData?.singlePreview?.title;

  if (
    isFetching ||
    !viewerData ||
    isError ||
    !currentSeriesId ||
    !currentProductId
  ) {
    return (
      <div className="w-1200pxr">
        <PcViewerHeader
          title={productTitle}
          hasNavMenu={false}
          seriesId={currentSeriesId}
          showAgeGradeLabel={false}
        />
      </div>
    );
  }

  const showAgeGradeLabel =
    !hideAgeGradeLabel &&
    [SlideType.Comic, SlideType.Epub].includes(viewerData.item.slideType) &&
    viewerData.seriesItem.ageGrade === AgeGrade.Fifteen;

  return (
    <>
      <div
        className={twMerge('w-1200pxr', !isViewerMenuExposed && 'hidden')}
        {...putTiaraObjectProps(
          viewerData.item.slideType === SlideType.Comic ||
            viewerData.item.slideType === SlideType.Epub ||
            viewerData.item.slideType === SlideType.VideoOnly ||
            viewerData.item.slideType === SlideType.TalkHumor
            ? {
                click: {
                  layer1: '뷰어메뉴',
                },
                customProps: {
                  viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
                  is_shortdrama: isShortsDrama ? 'y' : undefined,
                },
              }
            : undefined
        )}
      >
        <PcViewerHeader
          isShortsDrama={isShortsDrama}
          title={viewerData.item.title}
          seriesId={currentSeriesId}
          productId={currentProductId}
          commentCount={commentCount ? commentCount : undefined}
          hasNavMenu={true}
          isPrevButtonEnabled={!!viewerData.prevItem}
          isNextButtonEnabled={!!viewerData.nextItem}
          hasViewTypeButton={
            viewerData.item.slideType === SlideType.Epub ||
            (viewerData.item.slideType === SlideType.Comic &&
              !isScrollOnlyImageViewer)
          }
          isScrollView={checkPcIsScrollView()}
          isNonstopWatching={isNonstopWatching}
          showAgeGradeLabel={showAgeGradeLabel}
          onClickViewTypeButton={onClickViewTypeButton}
          onClickNonStopWatchingButton={onClickNonStopWatchingButton}
          onClickPrevButton={handleMovePrevProduct}
          onClickNextButton={handleMoveNextProduct}
          prevButtonTiaraObject={
            viewerData.prevItem &&
            putTiaraObjectProps({
              eventMeta: {
                id: String(viewerData.prevItem.productId),
                type: 'productId',
              },
            })
          }
          nextButtonTiaraObject={
            viewerData.nextItem &&
            putTiaraObjectProps({
              eventMeta: {
                id: String(viewerData.nextItem.productId),
                type: 'productId',
              },
            })
          }
        />
      </div>
      {isOpenNonstopWatchingDialog && (
        <NonstopWatchingDialog
          isOpen={isOpenNonstopWatchingDialog}
          onClose={() => setIsOpenNonstopWatchingDialog(false)}
          onClickConfirm={handleNonstopWatchingOn}
          isMobile={false}
          isNonstopWatching={isNonstopWatching}
          isCashTypeViewer={isCashTypeViewer}
          isShortsDrama={isShortsDrama}
        />
      )}
    </>
  );
}
